import { useEffect, useRef, useState } from "react";
import { Loader2, SendHorizontal } from "lucide-react";
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { GlobalFormSchema } from "~/scheme/global_form_schema";
import { parseWithZod } from "@conform-to/zod";
import {
  FetcherWithComponents,
  useFetcher,
  useNavigate,
} from "@remix-run/react";
import {
  TELE_BOT_TOKEN,
  TELE_CH_ID,
  WHATSAPP_GREETING,
  WHATSAPP_NUM,
} from "~/assets/profile/profile";
import { Text4 } from "../typographies/Texts";
import { Heading2 } from "../typographies/Headings";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Input } from "../ui/input";

interface WhatsappGlobalInterface {
  imageUrl?: string;
  className?: string;
  currentPage?: string | undefined;
}
interface WhatsappGlobalProps {
  whatsappTextImage?: WhatsappGlobalInterface[];
  buttonText?: string;
  currentPage?: string;
}
export default function GlobalContactWhatsappFloating({
  buttonText = "Whatsapp Admin",
  currentPage,
}: WhatsappGlobalProps) {
  const fetcher: FetcherWithComponents<any> = useFetcher();
  const [isOpen, setIsOpen] = useState(false);
  const [form, fields] = useForm({
    shouldValidate: "onBlur",
    onValidate({ formData }) {
      const schema = GlobalFormSchema;
      return parseWithZod(formData, { schema });
    },
  });

  const [inputWhatsapp, setInputWhatsapp] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const closeModal = () => {
    setShowModal(false);
    navigate("/"); // Navigate to the home page
    handleSendToWhatsApp(inputWhatsapp);
    setInputWhatsapp("");
    setInputMessage("");
  };

  useEffect(() => {
    if (fetcher?.data?.msg === "success") {
      setShowModal(true);
    }
  }, [fetcher]);

  type FormErrors = {
    message: string;
    whatsapp: string[];
  };

  const [formErrors, setFormErrors] = useState<FormErrors>({
    message: "Pesan wajib diisi",
    whatsapp: [
      "Whatsapp wajib diisi, agar kami dapat segera membalas pesan penting Anda.",
    ], // Default error for WhatsApp
  });

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // Reset form errors initially
    setFormErrors({
      message: "",
      whatsapp: [],
    });

    // Prepare form data
    const formData = {
      whatsapp: inputWhatsapp,
      message: inputMessage,
    };

    // Validate form data
    const validation = GlobalFormSchema.safeParse(formData);

    if (!validation.success) {
      const errors = validation.error.format();
      // Set form errors based on validation results
      setFormErrors((prev) => ({
        ...prev,
        whatsapp: errors.whatsapp?._errors?.[0]
          ? [errors.whatsapp?._errors?.[0]]
          : [],
        message: "",
      }));

      return;
    }

    // If validation passes, submit the form
    fetcher.submit(event.currentTarget.form);
  };

  const isSubmitDisabled =
    !inputMessage.trim() ||
    !inputWhatsapp.trim() ||
    !!formErrors.whatsapp.length;
  const pageRef = useRef<string | undefined>(currentPage);

  useEffect(() => {
    if (currentPage) {
      pageRef.current = currentPage;
    }
  }, [currentPage]);

  const handleSendToWhatsApp = (inputedWhatsapp: string) => {
    const now = new Date();
    const date = now.toLocaleDateString();
    const time = now.toLocaleTimeString();
    const adminMessage = `Halo Admin arsinesia.com, saya mau konsultasi`;
    const adminWhatsApp = WHATSAPP_NUM;
    window.open(
      `https://wa.me/${adminWhatsApp}?text=${encodeURIComponent(adminMessage)}`,
      "_blank"
    );
    const whatsappLink = `https://wa.me/${inputedWhatsapp}?text=${encodeURIComponent(
      `${WHATSAPP_GREETING}\nAda yang bisa saya bantu?\n☺️🙏`
    )}`;

    console.log(
      "🔍 Using pageRef.current in Telegram Message:",
      pageRef.current
    );

    const telegramMessage = `🟢NON-IKLAN\nTanggal: ${date}\nJam: ${time}\nWhatsapp: ${inputedWhatsapp}\nLink Whatsapp: ${whatsappLink}\nDari Halaman: ${
      pageRef.current || "Unknown Page"
    }`;

    console.log("📩 Sending to Telegram:", telegramMessage);

    const botToken = TELE_BOT_TOKEN;
    const chanId = TELE_CH_ID;
    const telegramApiUrl = `https://api.telegram.org/bot${botToken}/sendMessage`;

    fetch(telegramApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        chat_id: chanId,
        text: telegramMessage,
      }),
    })
      .then((response) => response.json())
      .then((data) => console.log("✅ Message sent to Telegram:", data))
      .catch((error) => console.error("❌ Error sending to Telegram:", error));
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const maxCharacters = 200; // Maximum allowed characters
  // Function to adjust the height of the textarea
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      // Reset the height to auto to calculate the new height
      textareaRef.current.style.height = "auto";
      // Set the height to the scrollHeight (content height)
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  const sheetRef = useRef<HTMLDivElement>(null);
  const adjustSheetHeight = () => {
    if (sheetRef.current) {
      const contentHeight = sheetRef.current.scrollHeight;
      sheetRef.current.style.height = `${contentHeight}px`;
    }
  };

  // Adjust the height of the Sheet whenever the content changes
  useEffect(() => {
    adjustSheetHeight();
  }, [inputWhatsapp, inputMessage, formErrors, showModal]);
  const getFormattedDate = () => {
    const date = new Date(); // Get the current date
    const day = date.getDate(); // Get the day of the month (1-31)
    const month = date.toLocaleString("id-ID", { month: "long" }); // Get the full month name in Indonesian
    const year = date.getFullYear(); // Get the full year (e.g., 2023)

    return `${day} ${month} ${year}`; // Format the date as "Tanggal [day] [month] [year]"
  };
  const getFormattedTime = () => {
    const date = new Date(); // Get the current date and time
    const hours = date.getHours(); // Get the current hour (0-23)
    const minutes = date.getMinutes(); // Get the current minute (0-59)

    // Format hours and minutes to always be two digits (e.g., "09:05")
    const formattedHours = String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`; // Format the time as "HH:MM"
  };
  return (
    <>
      {/* Floating Button */}
      <div className="w-full ">
        <Button
          onClick={() => setIsOpen(true)}
          className="mt-2 w-full min-h-[3rem] bg-yellow-500 rounded-lg  shadow-lg hover:bg-green-600"
        >
          <img
            className="w-[2rem] h-[2rem]"
            src="https://ik.imagekit.io/metame/saranabisnis_jasa-pendirian-pt-di-semarang/whatsapp-gif.gif?updatedAt=1735835712357"
            alt="WhatsApp logo"
            loading="lazy"
          />
          <div className="flex flex-row gap-1">
            <p className="text-md font-semibold text-black">{buttonText}</p>
          </div>
        </Button>
      </div>

      {/* Sheet (Drawer) */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          ref={sheetRef}
          className="bg-white pt-[3rem] px-[1rem] self-end rounded-lg w-full lg:w-fit"
          style={{ height: "auto" }} // Set height to auto initially
        >
          <SheetHeader className="text-start">
            <SheetTitle>Chat Tim Ahli arsinesia.com [GRATIS]</SheetTitle>
            <SheetDescription>
              Kami siap membantu Anda. Tulis pesan chat Anda di kolom, admin
              akan segera membalas chat Anda !
            </SheetDescription>
          </SheetHeader>
          <fetcher.Form method="POST" {...getFormProps(form)}>
            <div className="typo-light-primary w-full flex flex-col gap-3 text-start">
              <label
                htmlFor="message"
                className="pt-[1.5rem] text-sm font-medium text-slate-900"
              >
                Whatsapp Anda
              </label>
              <Input
                placeholder="contoh : 08123456789"
                {...getInputProps(fields.whatsapp, { type: "number" })}
                id="whatsapp"
                value={inputWhatsapp}
                onChange={(e) => {
                  const value = e.target.value;
                  setInputWhatsapp(value);

                  // Validate the Whatsapp number
                  const errors: string[] = [];
                  if (!value.startsWith("08")) {
                    errors.push(
                      'Format nomor salah, awali dengan "08", contoh: 085123123123.'
                    );
                  }
                  if (value.length < 9) {
                    errors.push(
                      "Jumlah nomor whatsapp terlalu pendek, Isi dengan nomor whatsapp aktif."
                    );
                  }
                  if (value.length > 14) {
                    errors.push(
                      "Jumlah nomor whatsapp terlalu panjang, Isi dengan nomor whatsapp aktif."
                    );
                  }

                  // Update form errors for whatsapp
                  setFormErrors((prev) => ({
                    ...prev,
                    whatsapp: errors, // Assign the array of errors
                  }));
                }}
                className={` -mt-2 text-slate-900 h-[2.7rem] w-full p-2 border rounded-md focus:ring-light-primary focus:border-light-primary ${
                  formErrors.whatsapp.length > 0 ? "border-red-500" : ""
                }`}
                required
              />

              {/* Display Errors for WhatsApp */}
              {formErrors.whatsapp.length > 0 && (
                <ul className="-mt-2 ml-1 text-red-500 text-sm list-disc list-inside">
                  {formErrors.whatsapp.map((error, index) => (
                    <li key={index} className="text-red-400">
                      {error}
                    </li>
                  ))}
                </ul>
              )}

              {/* Message Input */}
              <section
                className="rounded-md mt-[1.5rem] p-[1rem] w-full flex flex-col gap-3 text-start relative"
                style={{
                  backgroundImage:
                    "url('https://ik.imagekit.io/didigi/didigi/pt-didigi-kreasi-solusindo/didigi_pt-digify-kreasi-solusindo_bg-wa_jasa-pembuatan-website-di-semarang.webp')",
                  backgroundSize: "cover", // Ensure the image covers the entire section
                  backgroundPosition: "center", // Center the image
                  backgroundRepeat: "no-repeat", // Prevent the image from repeating
                }}
              >
                <div className="absolute bg-slate-800 rounded-t-md text-white flex flex-row items-center min-h-[4.5rem] top-0 left-0 w-full px-[1rem] py-[1rem]">
                  <img
                    className="min-w-[3.5rem] max-w-[3.5rem] z-10 pr-[1rem]"
                    src="https://ik.imagekit.io/didigi/didigi/pt-didigi-kreasi-solusindo/didigi_pt-digify-kreasi-solusindo_bg-wa_jasa-pembuatan-website-di-semarang-whatsapp.webp"
                  />
                  <p className="text-lg">Admin Ayu</p>
                </div>
                {/* Content */}
                <section className="relative z-10 flex flex-col">
                  <div className="pt-[5rem] ">
                    <p className="text-slate-300 text-sm text-center pb-3">
                      {getFormattedDate()}
                    </p>
                  </div>
                  <div className="bg-slate-300 w-fit px-3 py-2  rounded-r-lg rounded-t-lg">
                    <p className="text-sm">
                      Halo, saya Ayu. Ada yang bisa saya bantu?
                    </p>
                    <p className="text-end text-xs mt-2 text-slate-600">
                      {getFormattedTime()}
                    </p>
                  </div>
                  <div className="pt-[4rem] flex flex-row gap-2 items-center">
                    <textarea
                      ref={textareaRef}
                      id="message"
                      placeholder="Tulis pesan Anda di sini"
                      value={inputMessage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= maxCharacters) {
                          setInputMessage(value);
                          if (value.trim() === "") {
                            setFormErrors((prev) => ({
                              ...prev,
                              message: "Pesan tidak boleh kosong",
                            }));
                          } else {
                            setFormErrors((prev) => ({
                              ...prev,
                              message: "",
                            }));
                          }
                        }
                      }}
                      className={`pl-4 text-slate-900 rounded-full w-full p-2 border  focus:ring-light-primary focus:border-light-primary ${
                        formErrors.message ? "border-red-500" : ""
                      }`}
                      rows={1}
                      style={{ overflow: "hidden", resize: "none" }}
                      maxLength={maxCharacters}
                      required
                    />
                    {/* Character Count */}
                    <button
                      type="submit"
                      className={`w-fit ${
                        isSubmitDisabled ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      onClick={handleSubmit}
                      disabled={
                        isSubmitDisabled || fetcher.state === "submitting"
                      }
                    >
                      {fetcher.state === "submitting" ? (
                        <div className="bg-green-600 p-2 rounded-full">
                          <Loader2 className="text-slate-300 animate-spin" />{" "}
                          {/* Spinner */}
                        </div>
                      ) : (
                        <div className="bg-green-600 p-2 rounded-full">
                          <SendHorizontal className="text-slate-300" />{" "}
                          {/* Send icon */}
                        </div>
                      )}
                    </button>
                  </div>
                  <div className="py-2 text-sm text-slate-500 text-start">
                    {inputMessage.length}/{maxCharacters}
                  </div>
                  {formErrors.message && (
                    <Text4
                      className="text-red-400 -mt-2"
                      text={formErrors.message}
                    />
                  )}
                </section>
              </section>
            </div>
          </fetcher.Form>
          <section className=" p-[0.2rem] rounded-lg">
            {showModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="flex flex-col justify-center bg-white p-8 mx-8 rounded-lg shadow-lg max-w-md text-center">
                  <img
                    className="self-center w-20"
                    src="https://ik.imagekit.io/01/sagopro/sagopro-success.webp?updatedAt=1719313103186"
                    alt="Success"
                  />
                  <Heading2
                    className="typo-light-primary font-semibold"
                    text="Berhasil !"
                  />
                  <Text4
                    className="typo-light-primary"
                    text="Terima kasih telah menghubungi kami. Kami akan mengirimkan jadwal konsultasi untuk Anda."
                  />
                  <button
                    onClick={closeModal}
                    className="item-image mt-4 bg-black inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-light-primary"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            )}
          </section>
        </SheetContent>
      </Sheet>
    </>
  );
}
